import { faShoppingCart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useShoppingCartsContext } from 'context/ShoppingCartsContext';
import { useEffect } from 'react';
import { tss } from 'tss-react';

import * as State from 'components/desktop/ShoppingCart/_general/AddToCartModal/State';
import useState from 'components/desktop/ShoppingCart/_general/AddToCartModal/useState';
import Modal from 'components/desktop/_general/Modal/Modal';
import delay from 'functions/delay';
import useTranslation from 'hooks/useTranslation';

const AddToCartModal = () => {
	const { classes, cx } = useStyles();

	const { state, handlers } = useState();
	const { modalId, entityType, openModal, setModalId, ...carts } = useShoppingCartsContext();
	const { tl, t } = useTranslation();
	const defaultCart = carts.getDefault();

	useEffect(() => {
		if (!modalId) return;
		if (!entityType) return;
		handlers.initialize({
			id: modalId,
			entity: entityType,
		});
	}, [modalId, entityType]);

	const renderSelectProductMapping = (): JSX.Element => {
		return (
			<div>
				<label htmlFor="shoppingCartMapping" className={cx('form-label', classes.label)}>
					{t('recipe:PRODUCT_MAPPING')}
				</label>
				<select
					className="form-select"
					id="shoppingCartMapping"
					value={state.selectedErpRecipeId}
					onChange={(e) => handlers.changeSelectedErpRecipeId(e.target.value)}
				>
					<option value="">Default</option>
					{(state.erpRecipes ?? []).map((e, i) => (
						<option value={e.id} key={i}>
							{e.name}
						</option>
					))}
				</select>
			</div>
		);
	};

	const renderSelectShoppingCart = (): JSX.Element => {
		return (
			<div>
				<label htmlFor="shoppingCart" className={cx('form-label', classes.label)}>
					{t('_general:SHOPPING_CART')}
				</label>
				<select
					className="form-select"
					id="shoppingCart"
					value={state.selectedShoppingCartId}
					onChange={(e) => handlers.changeSelectedShoppingCartId(e.target.value)}
				>
					{(carts.getAllLive() ?? []).map((e, i) => (
						<option value={e.id} key={i}>
							{tl(e.name)} {e.default ? ' (default)' : ''}
						</option>
					))}
				</select>
			</div>
		);
	};

	const renderInput = (): JSX.Element => (
		<div>
			<label htmlFor="shoppingCartAmount" className={cx('form-label', classes.label)}>
				{t('_general:AMOUNT')}
			</label>
			<input
				type="number"
				className="form-control"
				id="shoppingCartAmount"
				value={state.amount}
				min={1}
				onChange={(e) => handlers.changeAmount(Number(e.target.value))}
			/>
		</div>
	);

	return (
		<Modal
			isOpen={Boolean(modalId)}
			close={() => {
				setModalId(null);
			}}
			size={'sm'}
			bodyContent={
				<div className={classes.container}>
					{renderSelectShoppingCart()}
					{renderSelectProductMapping()}
					{renderInput()}
				</div>
			}
			headerIcon={<FontAwesomeIcon icon={faShoppingCart}></FontAwesomeIcon>}
			buttonOutlined={{
				label: t('_general:BTN_LEAVE'),
				onClick: () => setModalId(null),
			}}
			buttonFull={{
				label: t('_general:ADD'),
				onClick: async () => {
					const basketId: string | null = State.getId(state);
					const basketIds: string[] = basketId ? [basketId] : [];
					if (!entityType) return;
					if (!state.id) return;
					await carts.addEntityToCart(
						{
							id: state.id,
							type: entityType,
							amount: state.amount,
							basketIds: basketIds,
						},
						state.selectedShoppingCartId
					);
					await delay(500);
					handlers.changeAmount(1);
				},
			}}
			headerText={
				defaultCart
					? t('shoppingCart:ADD_TO_CART') + ': ' + tl(defaultCart?.name)
					: t('shoppingCart:ADD_TO_CART')
			}
		></Modal>
	);
};

export default AddToCartModal;

export const useStyles = tss.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: '10px',
	},
	label: {
		marginBottom: '0',
	},
});
